/**
 * <ClarificationReplyDialog>
 *
 * Figma 9291-14719: 卡涩问题回复弹窗。
 * 左侧 Questions 列表 + 右侧详情 & 回复区。
 *
 * 数据流：
 *  - 列表数据由父组件 ClarificationBoard 通过 items prop 传入
 *  - getClarificationDetailGet → 右侧选中问题详情
 *  - replyClarificationPost → 提交回复
 *  - 提交成功后通过 onItemReplied 通知父组件移除条目，默认选中下一条
 */

import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import cn from 'classnames';
import { Button, Dialog, DialogContent, Textarea, toast } from '@leadsnavi/ui-kit';
import { useMemoizedFn } from 'ahooks';
import { X } from 'lucide-react';
import { PagePath } from '@/routes/paths';
import { getClarificationDetailGet, replyClarificationPost } from '@/services/swagger/easourcing/TaskReplyService';
import type { IApiClarificationResp, IApiClarificationDetailResp } from '@/services/swagger/easourcing/models';

export type ClarificationReplyDialogProps = {
  open: boolean;
  /** 由 ClarificationBoard 传入的完整列表 */
  items: IApiClarificationResp[];
  /** 初始选中的问题 ID */
  selectedId: string | null;
  onClose: () => void;
  /** 单条回复成功后回调（父组件负责从列表中移除） */
  onItemReplied: (repliedId: string) => void;
  /** 全部问题处理完成后的回调 */
  onAllDone?: () => void;
  className?: string;
};

export const ClarificationReplyDialog: FC<ClarificationReplyDialogProps> = ({
  open,
  items,
  selectedId: initialSelectedId,
  onClose,
  onItemReplied,
  onAllDone,
  className,
}) => {
  const navigate = useNavigate();

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [activeId, setActiveId] = useState<string | null>(null);
  const [detail, setDetail] = useState<IApiClarificationDetailResp | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // ---------------------------------------------------------------------------
  // Init: open 时设置选中项（仅在弹窗打开瞬间初始化，items 变化不重置）
  // ---------------------------------------------------------------------------
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      // 弹窗刚打开：初始化选中项
      setActiveId(initialSelectedId ?? (items.length > 0 ? items[0].clarificationId : null));
      setText('');
      setDetail(null);
    } else if (open && prevOpenRef.current) {
      // 弹窗已打开：items 或 selectedId 变化时校验当前选中项是否仍存在于列表
      setActiveId(prev => {
        const candidateId = prev ?? initialSelectedId;
        const existsInList = items.some(i => i.clarificationId === candidateId);
        if (existsInList) return candidateId;
        return items.length > 0 ? items[0].clarificationId : null;
      });
    } else if (!open && prevOpenRef.current) {
      // 弹窗刚关闭：重置所有状态
      setActiveId(null);
      setDetail(null);
      setText('');
      setSubmitting(false);
    }
    prevOpenRef.current = open;
  }, [open, initialSelectedId, items]);

  // ---------------------------------------------------------------------------
  // Fetch detail
  // ---------------------------------------------------------------------------
  const fetchDetail = useCallback(async (clarificationId: string) => {
    setLoadingDetail(true);
    setDetail(null);
    try {
      const res = await getClarificationDetailGet({ clarificationId: clarificationId });
      if (res?.data) {
        setDetail(res.data);
      }
    } catch {
      console.error('Failed to fetch clarification detail');
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // 选中变化时拉取详情 & 重置输入
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!activeId || !open) {
      setDetail(null);
      return;
    }
    setText('');
    void fetchDetail(activeId);
  }, [activeId, open, fetchDetail]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleOpenChange = useMemoizedFn((next: boolean) => {
    if (!next && !submitting) {
      onClose();
    }
  });

  const handleSelectItem = useMemoizedFn((id: string) => {
    if (id !== activeId) {
      setActiveId(id);
    }
  });

  const trimmedLen = text.trim().length;
  const sendDisabled = trimmedLen === 0 || submitting;

  const handleConfirmNext = useMemoizedFn(async () => {
    if (sendDisabled || !activeId) return;
    setSubmitting(true);
    try {
      await replyClarificationPost({
        clarificationId: activeId,
        userReply: text.trim(),
      });
      toast.success('Reply sent');

      const currentIndex = items.findIndex(i => i.clarificationId === activeId);
      const remaining = items.filter(i => i.clarificationId !== activeId);

      // 通知父组件移除此条目
      onItemReplied(activeId);

      if (remaining.length === 0) {
        // 全部处理完毕
        onAllDone?.();
        return;
      }

      // 选中下一个（若当前是最后一个则选新列表的最后一个）
      const nextIndex = Math.min(currentIndex, remaining.length - 1);
      setActiveId(remaining[nextIndex].clarificationId);
      setText('');
    } catch {
      toast.error('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  });

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------
  const selectedItem = items.find(i => i.clarificationId === activeId);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          'w-[956px] max-w-[956px] h-[743px] flex flex-col p-0 rounded-[10px] overflow-hidden gap-0',
          'shadow-[0px_4px_6px_-4px_rgba(0,0,0,0.10),0px_10px_15px_-3px_rgba(0,0,0,0.10)]',
          className
        )}
        onOpenAutoFocus={e => e.preventDefault()}
        showCloseButton={false}
      >
        {/* ───── Header ───── */}
        <div className="relative px-4 py-5 flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-[#171717] tracking-tight leading-[1.2]">
              AI Auto-reply issues need manual clarify
            </h2>
            <span className="inline-flex items-center gap-2 bg-[#D1FAE5] text-[#065F46] rounded-full px-2 py-0.5 text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-[#065F46]" />
              Auto send
            </span>
          </div>
          <p className="text-base text-[#737373] leading-6">
            We have summarized the content that needs to be supplemented/confirmed by you in the current supplier's
            response.
          </p>
          {/* Close */}
          <button
            type="button"
            onClick={() => handleOpenChange(false)}
            className="absolute top-4 right-4 w-5 h-5 flex items-center justify-center text-[#737373] hover:text-[#171717] transition-colors"
            disabled={submitting}
          >
            <X size={20} />
          </button>
        </div>

        {/* ───── Body: two-panel ───── */}
        <div className="flex-1 flex gap-3.5 px-4 pb-4 min-h-0">
          {/* Left: Questions List */}
          <div className="w-[280px] shrink-0 border border-[#E5E5E5] rounded-lg p-3 flex flex-col gap-3 self-stretch overflow-hidden">
            <div className="text-sm font-medium text-[#262626]">Questions</div>

            <div className="flex-1 overflow-y-auto flex flex-col gap-3">
              {items.length === 0 ? (
                <div className="flex items-center justify-center py-10 text-sm text-[#737373]">
                  No clarification questions
                </div>
              ) : (
                items.map(item => {
                  const isSelected = item.clarificationId === activeId;
                  return (
                    <button
                      key={item.clarificationId}
                      type="button"
                      onClick={() => handleSelectItem(item.clarificationId)}
                      className={cn(
                        'w-full text-left rounded-lg border p-3 flex flex-col gap-1 transition-colors',
                        isSelected
                          ? 'bg-[rgba(236,253,245,0.5)] border-[rgba(55,228,166,0.5)]'
                          : 'border-[#E5E5E5] hover:border-[#D1D5DB] hover:bg-[#FAFAFA]'
                      )}
                    >
                      <span className="text-sm font-medium text-[#262626] leading-snug">{item.issueSummary}</span>
                      <span className="text-sm text-[#737373]">
                        Asked by {item.supplierCount} supplier{item.supplierCount !== 1 ? 's' : ''}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: Detail + Reply */}
          <div className="flex-1 border border-[#E5E5E5] rounded-lg p-3 pt-4 flex flex-col gap-4 self-stretch overflow-hidden min-w-0">
            {!selectedItem ? (
              <div className="flex items-center justify-center flex-1 text-sm text-[#737373]">
                Select a question from the left
              </div>
            ) : loadingDetail ? (
              <div className="flex items-center justify-center flex-1 text-sm text-[#737373]">Loading detail...</div>
            ) : (
              <>
                {/* Detail card */}
                <div className="flex-1 flex flex-col gap-4 overflow-hidden">
                  {/* Question & suppliers card */}
                  <div className="bg-[#FAFAFA] border border-[#F8F8F7] rounded-xl p-3 flex flex-col gap-3 overflow-y-auto max-h-[250px]">
                    {/* Question header */}
                    <div className="flex items-start gap-5">
                      <span className="text-sm font-medium text-[#111827] flex-1">
                        Q: {detail?.issueSummary ?? selectedItem.issueSummary}
                      </span>
                      <span className="text-sm text-[#737373] shrink-0">
                        Asked by {selectedItem.supplierCount} supplier
                        {selectedItem.supplierCount !== 1 ? 's' : ''}
                      </span>
                    </div>

                    {/* Supplier details */}
                    {detail?.contacts && detail.contacts.length > 0 && (
                      <div className="flex flex-col gap-2">
                        {detail.contacts.map(contact => (
                          <p key={contact.inquiryContactId} className="text-sm text-[#737373] leading-relaxed">
                            <span className="font-medium">{contact.companyName || contact.name}</span>
                            {contact.details ? `: "${contact.details}"` : ''}{' '}
                            <a
                              className="underline cursor-pointer hover:text-[#171717] transition-colors"
                              onClick={() => {
                                navigate(
                                  generatePath(PagePath.supplierDetail, { contactId: String(contact.contactId) }),
                                  { replace: true, state: { emailId: contact.triggerMsgId } }
                                );
                              }}
                            >
                              View Detail
                            </a>
                          </p>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Your answer section */}
                  <div className="flex flex-col gap-2 flex-1 min-h-0">
                    <span className="text-sm text-[#262626]">Your answer</span>
                    <Textarea
                      ref={textareaRef}
                      value={text}
                      onChange={e => setText(e.target.value)}
                      placeholder="Type your message here..."
                      disabled={submitting}
                      className={cn(
                        'flex-1 min-h-[200px]',
                        'text-sm leading-relaxed text-[#171717]',
                        'resize-none p-4 rounded-xl',
                        'border border-[#E5E5E5] bg-white',
                        'placeholder:text-[#A3A3A3]',
                        'focus-visible:ring-0 focus-visible:ring-offset-0'
                      )}
                    />
                    <p className="text-sm text-[#737373]">AI will draft and send replies after you confirm.</p>
                  </div>
                </div>

                {/* Footer: Confirm & Next */}
                <div className="flex justify-end">
                  <Button onClick={handleConfirmNext} loading={submitting} disabled={sendDisabled}>
                    Confirm & Next
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClarificationReplyDialog;
